import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, Redirect, useParams } from "react-router-dom";

import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";
import { BasePage } from "components/BasePage/BasePage";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import { CheckCircleIcon } from "@heroicons/react/solid";
import { StatusBadge } from "components/AIRiskAssessment/StatusBadge";
import { DraftSummary } from "components/AIRiskAssessment/AcceptGateModal";
import { HazardShellForm } from "components/AIRiskAssessment/HazardShellForm";
import { DiffReviewPanel } from "components/AIRiskAssessment/DiffReviewPanel";
import { HazardDetailEditor } from "components/AIRiskAssessment/HazardDetailEditor";
import { useAIRiskSession } from "components/AIRiskAssessment/hooks/useAIRiskSession";
import { AIHazardShell, AIHazardStatus } from "components/AIRiskAssessment/types";

interface AIHazardDetailRouteParams {
  projectId: string;
  hazardId: string;
}

// Flag guard FIRST (plan §4): with the feature flag off this page is inert —
// deep links bounce straight back to the ordinary risks list. The guard lives
// in its own component so the data hooks below never run (and never change
// hook order) while the flag is off.
export const AIHazardDetailPage = () => {
  const { projectId, hazardId } = useParams<AIHazardDetailRouteParams>();
  const { isFeatureEnabled } = useFeatureFlags();

  if (!isFeatureEnabled(Feature.AIRiskAssessment)) {
    return <Redirect to={`/projects/${projectId}/risks`} />;
  }

  return <AIHazardDetailContent projectId={projectId} hazardId={hazardId} />;
};

// Which view the detail page is in, per the plan §1 state machine:
// accepted → read-only summary; pending_proposal → diff view; draft → Phase B
// editor; otherwise (suggested, or dismissed without a draft) → Phase A shell.
const headlineKeyForHazard = (hazard: AIHazardShell): string => {
  if (hazard.status === AIHazardStatus.accepted) {
    return "risks.ai.detail.accepted.headline";
  }

  if (hazard.pending_proposal) {
    return "risks.ai.diff.headline";
  }

  if (hazard.draft) {
    return "risks.ai.detail.headline";
  }

  return "risks.ai.shell.headline";
};

interface AIHazardDetailContentProps {
  projectId: string;
  hazardId: string;
}

const AIHazardDetailContent = ({
  projectId,
  hazardId,
}: AIHazardDetailContentProps) => {
  const { t } = useTranslation();
  const hasTrackedOpen = useRef(false);
  const { track } = useContext(TrackingContext);
  const { displayNotification } = useContext(ControlContext);
  const { project, fetchInProgress } = useProjectDetails(projectId);
  const {
    data: session,
    isFetched,
    isLoading: sessionLoading,
  } = useAIRiskSession(projectId);

  const hazard = session?.hazards.find(({ id }) => id === hazardId);
  // Covers both an unknown hazard id and a missing session (null) once the
  // session query has settled.
  const hazardNotFound = isFetched && !hazard;

  // The hazard's component/subsystem group → RiskGroupping label on accept.
  const groupLabel =
    session?.groups.find((group) => group.hazard_ids.includes(hazardId))
      ?.label || "";

  // Deleted-risk fallback (plan §5): the accepted deep-link only renders
  // while the minted resource_id still exists in project.risks.
  const acceptedRiskExists =
    !!hazard?.accepted_risk_resource_id &&
    !!project?.risks?.some(
      (risk) => risk.resource_id === hazard.accepted_risk_resource_id
    );

  useEffect(() => {
    if (hazard && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      track("ai risk: open hazard", { status: hazard.status });
    }
  }, [hazard, track]);

  useEffect(() => {
    if (hazardNotFound) {
      displayNotification!(t("risks.ai.errors.hazardNotFound"), "", "error");
    }
  }, [hazardNotFound, displayNotification, t]);

  if (hazardNotFound) {
    return <Redirect to={`/projects/${projectId}/risks/ai`} />;
  }

  return (
    <BasePage
      breadcrumbItems={[
        { label: "projects", location: "/dashboard" },
        {
          label: project?.projectdata.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: "steps.risks.label",
          location: `/projects/${projectId}/risks`,
        },
        {
          label: "risks.ai.breadcrumb",
          location: `/projects/${projectId}/risks/ai`,
        },
        {
          label: hazard?.name || "risks.ai.breadcrumbHazard",
          location: `/projects/${projectId}/risks/ai/hazards/${hazardId}`,
        },
      ]}
      loading={fetchInProgress || sessionLoading}
    >
      {hazard && (
        <div>
          <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
            <h3 className="text-3xl leading-6 font-medium text-gray-900">
              {t(headlineKeyForHazard(hazard))}
            </h3>
          </div>

          {/* Context strip: status badge + back-to-board link always visible
              immediately below the heading separator. */}
          <div className="mt-3 flex items-center justify-between">
            <Link
              to={`/projects/${projectId}/risks/ai`}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← {t("risks.ai.detail.backToBoard")}
            </Link>
            <StatusBadge
              status={hazard.status}
              acceptedRiskMissing={
                hazard.status === AIHazardStatus.accepted && !acceptedRiskExists
              }
            />
          </div>

          {/* Branches per the §1 state machine: accepted-readonly / diff view
              (persisted pending_proposal, incl. after reload) / Phase B
              editor (draft) / Phase A shell (suggested or
              dismissed-without-draft). */}
          {hazard.status === AIHazardStatus.accepted ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t("risks.ai.detail.accepted.headline")}
                  </p>
                  <p className="text-sm text-green-700">
                    {t("risks.ai.detail.accepted.subline")}
                  </p>
                  {acceptedRiskExists && (
                    <Link
                      to={`/projects/${projectId}/risks/${hazard.accepted_risk_resource_id}/edit`}
                      className="mt-2 inline-block text-sm font-medium text-green-800 underline hover:text-green-900"
                    >
                      {t("risks.ai.card.viewRisk")}
                    </Link>
                  )}
                </div>
              </div>
              {hazard.draft && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <DraftSummary draft={hazard.draft} />
                </div>
              )}
            </div>
          ) : hazard.pending_proposal && hazard.draft ? (
            <DiffReviewPanel projectId={projectId} hazard={hazard} />
          ) : hazard.draft ? (
            <HazardDetailEditor
              projectId={projectId}
              project={project}
              hazard={hazard}
              groupLabel={groupLabel}
            />
          ) : (
            <>
              <p className="mt-4 text-sm text-gray-500">
                {t("risks.ai.shell.subline")}
              </p>
              <HazardShellForm projectId={projectId} hazard={hazard} />
            </>
          )}
        </div>
      )}
    </BasePage>
  );
};
